import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../hooks/redux";
import { useAction } from "../../../hooks/actions";
import { useLazyGetControllersTableQuery } from "../../../middleware/controllersApi";
import { formFields, getColumns } from "./form-config";
import ObjectDialog from "../ObjectsList/ObjectDialog";
import { ModalDebugging } from "./ModalDebugging";
import { MoreObjectDialog } from "./MoreObjectDialog";
import { getQueryString } from "../../../helpers/tableHelper";
import Header from "./Header";
import { EnhancedTableHead } from "../ObjectsList/TableBasic/Filters";
import TableBasic from "../ObjectsList/TableBasic";
import "./styles/style.css";

const Controllers = () => {
  const tableNameSearchParams = "controllerListSearchParams";
  const { consoleNameStorage, protectionObjectIdStorage, pageNumberStorage } =
    JSON.parse(sessionStorage.getItem(tableNameSearchParams)) || {};
  const { t } = useTranslation();
  const { allControllers } = useAppSelector((state) => state.controllers);
  const { setIsErrorRequest } = useAction();
  const [pageNumber, setPageNumber] = useState(pageNumberStorage || 0);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [selectedObjectList, setSelectedObjectList] = useState([]);
  const [openModalDebbuging, setOpenModalDebbuging] = useState(false);
  const [openObjectDialog, setOpenObjectDialog] = useState(false);
  const [openMoreObjectDialog, setOpenMoreObjectDialog] = useState(false);
  const refreshIntervalRef = useRef(null);

  const [getControllersTable, { isLoading, error }] = useLazyGetControllersTableQuery();

  const { control, setValue, reset } = useForm({
    defaultValues: {
      [formFields.searchParams]: {
        [formFields.consoleName]: consoleNameStorage || "",
        [formFields.protectionObjectId]: protectionObjectIdStorage || "",
      },
    },
  });

  const params = useWatch({
    control,
    name: formFields.searchParams,
  });

  const getControllers = (isZero = false) => {
    const filters = getQueryString(params);
    getControllersTable({ filters, pageNumber: isZero ? 0 : pageNumber });
  };

  const refreshIntervalList = () => {
    // Очищаем предыдущий интервал
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Запускаем новый интервал
    refreshIntervalRef.current = setInterval(getControllers, 5000);

    // Очищаем интервал при размонтировании компонента
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  };

  useEffect(() => {
    getControllers(true);
    refreshIntervalList();
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [params]);

  useEffect(() => {
    getControllers();
    refreshIntervalList();
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [pageNumber]);

  function writeSearchDataToSessionStorage(newSearchParam, searchValue) {
    const getStorage = JSON.parse(sessionStorage.getItem(tableNameSearchParams)) || {};
    const searchParamsForLocalStorage = {
      ...getStorage,
      [newSearchParam]: searchValue,
    };
    sessionStorage.setItem(tableNameSearchParams, JSON.stringify(searchParamsForLocalStorage));
  }

  function clearFiltersHandler() {
    setPageNumber(0);
    reset({
      [formFields.searchParams]: {
        [formFields.consoleName]: "",
        [formFields.protectionObjectId]: "",
      },
    });
    sessionStorage.removeItem(tableNameSearchParams);
  }

  function savePageNumberToSessionStorage(currentPageNumber) {
    const sessionStorageData = JSON.parse(sessionStorage.getItem(tableNameSearchParams)) || {};
    const searchParamsForLocalStorage = {
      ...sessionStorageData,
      pageNumberStorage: currentPageNumber,
    };
    sessionStorage.setItem(tableNameSearchParams, JSON.stringify(searchParamsForLocalStorage));
  }

  const handleOpenObjectDialogState = (id) => {
    setOpenObjectDialog(true);
    setSelectedObjectId(id);
  };
  const handleCloseObjectDialogState = (id) => {
    setOpenObjectDialog(false);
    setSelectedObjectId(null);
  };

  const columns = useMemo(
    () =>
      getColumns({
        control,
        tableNameSearchParams,
        setValue,
        writeSearchDataToSessionStorage,
      }),

    []
  );

  const rows = useMemo(() => {
    const { data } = allControllers;

    return data?.length
      ? data.map(({ id, name, facilities, status, hasDiagnostics }) => {
          return {
            id,
            name: {
              id: id,
              value: name,
            },
            facilities: {
              facilities:
                facilities.map(({ id, integrationNumber, description, name, isInTest }) => ({
                  id,
                  name: `${description || name}${integrationNumber !== "" ? ", " + integrationNumber : ""}${
                    isInTest ? ", " + t("label:isInTest") : ""
                  }`,
                  action: () => handleOpenObjectDialogState(id),
                })) || [],
              actionShowMore: () => {
                setSelectedObjectList(facilities);
                setOpenMoreObjectDialog(true);
                setSelectedName(name);
              },
            },
            status,
            hasDiagnostics: {
              id: id,
              value: hasDiagnostics,
            },
          };
        })
      : [];
  }, [allControllers]);

  useEffect(() => {
    setIsErrorRequest(error);
  }, [error]);

  const recordsCount = useMemo(
    () => allControllers && allControllers?.recordsCount,

    [allControllers]
  );

  const renderHeader = useMemo(() => <EnhancedTableHead columns={columns} />, [columns]);

  return (
    <Box className="controllers-container">
      <Header handleModalDebbuging={setOpenModalDebbuging} clearFiltersHandler={clearFiltersHandler} />

      {openObjectDialog && (
        <ObjectDialog open={openObjectDialog} onClose={handleCloseObjectDialogState} objectId={selectedObjectId} />
      )}

      <TableBasic
        renderHeader={renderHeader}
        withSearchField={false}
        columns={columns}
        rows={rows}
        handleClickCell={() => {}}
        pageNumber={pageNumber}
        changePageNumber={setPageNumber}
        isLoading={isLoading}
        count={recordsCount}
        savePageNumberToSessionStorage={savePageNumberToSessionStorage}
        minHeight={"59vh"}
        maxHeight={"70vh"}
      />

      {openModalDebbuging && <ModalDebugging open={openModalDebbuging} setOpen={setOpenModalDebbuging} />}

      {openMoreObjectDialog && (
        <MoreObjectDialog
          open={openMoreObjectDialog}
          setOpen={setOpenMoreObjectDialog}
          selectedObjectList={selectedObjectList}
          setSelectedObjectId={setSelectedObjectId}
          setOpenObjectDialog={setOpenObjectDialog}
          selectedName={selectedName}
        />
      )}
    </Box>
  );
};

export default Controllers;
